import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { UrlService } from './url.service';
import { QuestionnaireQuestionInstancesDto } from 'app/@types/dto/questionnaire-question-instances-dto.model';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { SectorBenchmarkDto } from 'app/@types/dto/sector-benchmark-dto.model';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';
import { QuestionnaireDimensionDto } from 'app/@types/dto/questionnaire-dimension-dto.model';
import { ConvertQuestionnaireTypeRequestDto } from 'app/@types/dto/convert-questionnaire-type-request-dto.model';
import { CopyToMultipleDestinationsRequestDto } from 'app/@types/dto/copy-to-multiple-destinations-request-dto.model';
import { CopyToMultipleDestinationsResponseDto } from 'app/@types/dto/copy-to-multiple-destinations-response-dto.model';

@Injectable({
    providedIn: 'root',
})
export class QuestionnaireHttpService {
    constructor(
        private http: HttpClient,
        private urlService: UrlService,
    ) {}

    getOrganization(orgId: string): Observable<OrganizationDto> {
        return this.http.get<OrganizationDto>(this.urlService.getOrganizationUrl(orgId));
    }

    get(orgId: string, year: number, entityId: string): Observable<QuestionnaireQuestionInstancesDto> {
        return this.http.get<QuestionnaireQuestionInstancesDto>(
            this.urlService.getQuestionnaire(orgId, year, entityId),
        );
    }

    getAll(orgId: string): Observable<QuestionnaireQuestionInstancesDto[]> {
        return this.http.get<QuestionnaireQuestionInstancesDto[]>(this.urlService.getAllQuestionnaires(orgId));
    }

    createUpdate(orgId: string, qi: QuestionInstanceDto): Observable<QuestionInstanceDto[]> {
        return this.http.put<QuestionInstanceDto[]>(this.urlService.createUpdateQuestionInstance(orgId), qi);
    }

    createQuestionnaireFromPeriod(orgId: string, year: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.createQuestionnaireFromPeriod(orgId), year);
    }

    createEntity(orgId: string, entity: OrganizationEntityDto): Observable<OrganizationEntityDto> {
        return this.http.post<OrganizationEntityDto>(this.urlService.getOrganizationEntityUrl(orgId), entity);
    }

    updateEntity(orgId: string, orgEntityId: string, entity: OrganizationEntityDto): Observable<OrganizationEntityDto> {
        return this.http.put<OrganizationEntityDto>(
            this.urlService.getOrganizationEntityUrl(orgId, orgEntityId),
            entity,
        );
    }

    deleteEntity(orgId: string, entityId: string): Observable<QuestionnaireDto[]> {
        return this.http.delete<QuestionnaireDto[]>(this.urlService.getOrganizationEntityUrl(orgId, entityId));
    }

    getEntities(orgId: string): Observable<OrganizationEntityDto[]> {
        return this.http.get<OrganizationEntityDto[]>(this.urlService.getOrganizationEntityUrl(orgId));
    }

    deleteQuestionnaireFromPeriod(orgId: string, year: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.deleteQuestionnaireFromPeriod(orgId), year);
    }

    editQuestionnairesPeriod(orgId: string, currentyear: number, newYear: number): Observable<QuestionnaireDto[]> {
        return this.http.put<QuestionnaireDto[]>(this.urlService.editQuestionnairesPeriod(orgId), {
            currentyear,
            newYear,
        });
    }

    deleteQuestionnaire(orgId: string, questionnaireId: string) {
        return this.http.delete(this.urlService.deleteQuestionnaire(orgId, questionnaireId));
    }

    hardDeleteQuestionnaire(orgId: string, questionnaireId: string) {
        return this.http.delete(this.urlService.hardDeleteQuestionnaire(orgId, questionnaireId));
    }

    updateProperty(orgId: string, questionnaire: QuestionnaireDto): Observable<QuestionnaireDto> {
        return this.http.put<QuestionnaireDto>(this.urlService.updateQuestionnaire(orgId), questionnaire);
    }

    resetContent(orgId: string, questionnaireId: string): Observable<QuestionnaireDto> {
        return this.http.put<QuestionnaireDto>(this.urlService.resetQuestionnaireContent(orgId, questionnaireId), {});
    }

    convertType(orgId: string, questionnaireId: string, targetTypeId: number): Observable<QuestionnaireDto> {
        const body: ConvertQuestionnaireTypeRequestDto = { targetTypeId };
        return this.http.put<QuestionnaireDto>(this.urlService.convertQuestionnaireType(orgId, questionnaireId), body);
    }

    // Lock/unlock return 200 with no body — the matrix flips the `isLocked` flag locally.
    lock(orgId: string, questionnaireId: string): Observable<void> {
        return this.http.put<void>(this.urlService.lockQuestionnaire(orgId, questionnaireId), {});
    }

    unlock(orgId: string, questionnaireId: string): Observable<void> {
        return this.http.put<void>(this.urlService.unlockQuestionnaire(orgId, questionnaireId), {});
    }

    copyScanMulti(
        orgId: string,
        sourceEntityId: string,
        year: number,
        destinationOrganizationEntityIds: string[],
        sourceYear?: number,
    ): Observable<CopyToMultipleDestinationsResponseDto> {
        const body: CopyToMultipleDestinationsRequestDto = {
            destinationOrganizationEntityIds,
            year,
            // Only forward sourceYear when it differs from the target year — keeps the wire payload
            // identical for same-year callers and lets the backend skip the cross-year branch.
            ...(sourceYear !== undefined && sourceYear !== year ? { sourceYear } : {}),
            questionnaireRowIds: [],
        };
        return this.http.post<CopyToMultipleDestinationsResponseDto>(
            this.urlService.copyScanQuestionnaireMulti(orgId, sourceEntityId),
            body,
        );
    }

    delete(orgId: string, qi: QuestionInstanceDto): Observable<QuestionInstanceDto[]> {
        return this.http.delete<QuestionInstanceDto[]>(this.urlService.deleteQuestionInstance(orgId), { body: qi });
    }

    getBenchmark(sectorId: string, entityId: string): Observable<SectorBenchmarkDto> {
        return this.http.get<SectorBenchmarkDto>(this.urlService.getBenchmark(sectorId, entityId));
    }

    getCurrencies(orgId: string): Observable<CurrencyDto[]> {
        return this.http.get<CurrencyDto[]>(this.urlService.getCurrencies(orgId));
    }

    getSummaryDoc(orgId: string, questionnaireId: string): Observable<{ [dimension: string]: number }> {
        return this.http.put<{ [dimension: string]: number }>(
            this.urlService.getSummaryDoc(orgId, questionnaireId),
            {},
        );
    }

    createQuestionnaireDimension(
        orgId: string,
        questionnaireDimension: QuestionnaireDimensionDto,
    ): Observable<QuestionnaireDimensionDto> {
        return this.http.post<QuestionnaireDimensionDto>(
            this.urlService.createQuestionnaireDimension(orgId),
            questionnaireDimension,
        );
    }

    updateQuestionnaireDimension(
        orgId: string,
        questionnaireDimension: QuestionnaireDimensionDto,
    ): Observable<QuestionnaireDimensionDto> {
        return this.http.put<QuestionnaireDimensionDto>(
            this.urlService.createQuestionnaireDimension(orgId),
            questionnaireDimension,
        );
    }
}
